import {
  CiteElementDisplayMode,
  ImageCaptureMode,
  InspectionItemStatus,
} from '@main-application/inspections/models/rest-inspections-model.interface';
import { CustomDropDownOption } from '@main-application/management/pages/property-model/services/custom-fields.service';
import { CompulsoryItems } from '@main-application/shared/template-editor-dialog/models/template-editor-model';

export interface SurveyJSON {
  title: string;
  logoPosition: string;
  pages: SurveyPage[];
  instructions: string;
  showInstructions: boolean;
}

export interface SurveyPage {
  uuid: string;
  name: string;
  title: string;
  elements: ResidentInspectionArea[];
  pageType?: string;
  compulsoryItems: CompulsoryItems[];
  instructions: string;
  mainImage: { fileUploadId: number }[];
  showInstructions?: boolean;
  imageCaptureMode?: ImageCaptureMode;
}

export interface ResidentInspection {
  id: number;
  unitName: string;
  propertyName: string;
  inspectionName: string;
  isCompleted: boolean;
  completedAt: string;
  timeSinceUpdate: string;
  areas: ResidentInspectionArea[];
  showInstructions?: boolean;
  instructionsText?: string;
  residentSignatureRequired?: boolean;
  modifiedAt?: Date;
}

export interface ResidentInspectionArea {
  id: number;
  inspectionId: number;
  inspectionTemplateAreaId: number;
  guid?: string;
  uuid: string;
  status: InspectionItemStatus;
  type?: string;
  name?: string;
  title?: string;
  areaName: string;
  items?: ResidentInspectionItem[];
  defaultValue?: boolean;
  value?: string;
  elements?: ResidentInspectionArea[];
  mainImage?: { fileUploadId: number }[];
  description?: string;
  areaInstructions?: string;
  showInstructions?: boolean;
  pageType?: string;
  options?: any;
  customFieldBinding?: number;
  modifiedAt?: Date;
  visible?: boolean | null;
  isRequired?: boolean | null;
  displayMode?: CiteElementDisplayMode | null;
  imageCaptureMode?: ImageCaptureMode | null;
  photoRequiresForAllitems?: boolean;
  commentsRequiresForAllitems?: boolean;
  photoRequiresOn?: string[];
  commentsRequiresOn?: string[];
  orientation?: RadioListOrientation;
}

export interface ResidentInspectionItem {
  id: number;
  uuid: string;
  guid?: string;
  title?: string;
  emoji?: string;
  text?: string;
  isDefault?: boolean;
  inspectionId: number;
  inspectionTemplateAreaId: number;
  status: InspectionItemStatus;
  itemName: string;
  emojiDetails: EmojiDetails[];
  booleanQuestions: BooleanQuestion[];
  texts: CustomField[];
  numbers: CustomField[];
  ints: CustomField[];
  moneys: CustomField[];
  questions?: CustomField[];
  dates: CustomField[];
  dateTimes: CustomField[];
  dropdowns: CustomField[];
  checklists: CustomField[];
  radiolists: CustomField[];
  fileUploadIds: number[];
  fileUploadIdsInspectionContent?: number[];
  images: ImageDetails[];
  comment: string;
  surveyJSON: SurveyJSON;
  photoVisible?: boolean;
  photoRequired: boolean;
  imageCaptureMode?: ImageCaptureMode | null;
  commentVisible?: boolean;
  commentRequired: boolean;
  instructions: string;
  value: string;
  onSelect?: CompulsoryItems[];
  item?: ResidentInspectionItemDetails;
  showInstructions?: boolean;
  pageType?: string;
  order?: { order: number; guid: string }[];
}

export interface ResidentInspectionItemDetails {
  id: string;
  text?: string;
  emoji: string;
  backgroundColor?: string;
  isDefault?: boolean;
  materialsMultiplier?: number;
  onSelect?: CompulsoryItems[];
  resultScore?: number;
  showHoursWidget?: boolean;
  showQtyWidget?: boolean;
  timeMultiplier?: number;
}

export interface EmojiDetails {
  guid: string;
  emojiTitle?: string;
  emojiCode: string;
  isDefault: boolean;
  emojiName: string;
  onSelect?: CompulsoryItems[];
}

export interface BooleanQuestion {
  guid: string;
  question: string;
  answer?: string;
  defaultAnswer?: string;
  customFieldBinding?: number;
  isRequired?: boolean | null;
  displayMode?: CiteElementDisplayMode | null;
}

export interface CustomField {
  type?: 'number' | 'boolean' | 'text' | 'money' | 'int' | 'user' | 'date' | 'dateTime' | 'checkList' | 'dropdown';
  guid: string;
  title: string;
  value?: string;
  defaultValue?: string;
  customFieldBinding?: number;
  isRequired?: boolean | null;
  displayMode?: CiteElementDisplayMode | null;
  options?: CustomDropDownOption[]; // dropdown only
  valueDate?: Date; // dateTime only
  valueTime?: Date; // dateTime only
  photoRequiresForAllitems?: boolean; // checkList only
  commentsRequiresForAllitems?: boolean; // checkList only
  photoRequiresOn?: string[]; // checkList only
  commentsRequiresOn?: string[]; // checkList only
  orientation?: RadioListOrientation;
}

export interface CustomFieldParsed {
  type?: 'number' | 'boolean' | 'text' | 'money' | 'int' | 'user' | 'date' | 'dateTime' | 'checkList' | 'dropdown';
  guid: string;
  title: string;
  value?: string[];
  defaultValue?: string;
  customFieldBinding?: number;
  isRequired?: boolean | null;
  displayMode?: CiteElementDisplayMode | null;
  options?: CustomDropDownOption[]; // dropdown only
  valueDate?: Date; // dateTime only
  valueTime?: Date; // dateTime only
  photoRequiresForAllitems?: boolean; // checkList only
  commentsRequiresForAllitems?: boolean; // checkList only
  photoRequiresOn?: string[]; // checkList only
  commentsRequiresOn?: string[]; // checkList only
}

export interface ImageDetails {
  cloudUri: string;
  thumbnailUri?: string;
  videoDuration?: number;
}

export enum RadioListOrientation {
  Vertical,
  Horizontal,
}
